import { type ProductizedServiceCardDataWrapper$data } from '#app/__generated__/relay/ProductizedServiceCardDataWrapper.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { Link } from '#app/components/Link/index.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMediumScreen } from '#app/hooks/useIsMediumScreen.js';
import { ProductizedServiceCardPricing } from './ProductizedServiceCardPricing.js';
import { EditAltIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { ReactNode } from 'react';
import { css, styled } from 'styled-components';

const Details = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    flex-grow: 1;

    ${theme.mediaQueries.xl} {
    }
  `}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

type ServiceCardDetailsProps = {
  readonly actions: ReactNode;
  readonly position: number;
  readonly service: ProductizedServiceCardDataWrapper$data;
};

export const ProductizedServiceCardDetails = ({
  actions,
  position,
  service,
}: ServiceCardDetailsProps) => {
  const isBelowMedium = useIsMediumScreen();

  const { track } = useAnalytics();

  const {
    analyticsId,
    llmSuggestionId,
    price,
    publishedAt,
    slug,
    userProfile,
    visitorCanEdit,
  } = service;

  const isDraft = publishedAt === null;
  const isProfileMagicSuggestion = isDraft && llmSuggestionId !== null;

  const serviceLink =
    isProfileMagicSuggestion && visitorCanEdit
      ? getRoute('/s/@slug/edit', { args: { slug } })
      : getRoute('/s/@slug', {
          args: { slug },
        });

  const button = (
    <Button
      as={Link}
      block={isBelowMedium}
      icon={
        isProfileMagicSuggestion && visitorCanEdit ? EditAltIcon : undefined
      }
      onClick={() => {
        track('service_card', {
          action: 'tapped',
          cta: 'button',
          id: analyticsId,
          is_owner: visitorCanEdit,
          owner_id: userProfile.analyticsId,
          position_index: position,
          section: 'profile',
        });
      }}
      size={isBelowMedium ? 'medium' : 'small'}
      to={serviceLink}
      variant="V2Secondary"
    >
      {isProfileMagicSuggestion && visitorCanEdit
        ? 'Add content'
        : 'View Details'}
    </Button>
  );

  if (isBelowMedium) {
    return button;
  }

  return (
    <Details>
      <Container>
        <ProductizedServiceCardPricing price={price} />

        <ActionsRow>
          {button}

          {actions}
        </ActionsRow>
      </Container>
    </Details>
  );
};
